import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Plus, Search } from 'lucide-react'
import { ProjectGrid } from '@/components/video'
import { useCombinedVideos } from '@/hooks/useCombinedVideos'
import { cn } from '@/lib/utils'

export default function LibraryPage() {
  const [query, setQuery] = useState('')

  const { videos, isLoading: videosLoading, refetchLegacy } = useCombinedVideos()
  const workflows = useQuery(api.workflows.listForUser, {})
  const isLoading = videosLoading || workflows === undefined

  const allWorkflows = workflows ?? []
  const allVideos = videos ?? []

  const visibleWorkflows = useMemo(() => {
    return allWorkflows.filter((w) => {
      // Hide CLI/desktop-imported projects until they have a cover image
      const isExternal = w.source === 'cli' || w.source === 'desktop' || w.isDesktop
      if (isExternal && !w.coverImageUrl) return false
      if (!query) return true
      const haystack = `${w.title ?? ''} ${w.narrative ?? ''}`.toLowerCase()
      return haystack.includes(query.toLowerCase())
    })
  }, [allWorkflows, query])

  const visibleVideos = useMemo(() => {
    if (!query) return allVideos
    return allVideos.filter((v: any) => {
      const title = (v.title ?? v.prompt ?? '').toString().toLowerCase()
      return title.includes(query.toLowerCase())
    })
  }, [allVideos, query])

  const isEmpty = !isLoading && visibleWorkflows.length === 0 && visibleVideos.length === 0

  return (
    <div className="px-8 pt-10 pb-16">
      <Helmet>
        <title>Library — Footage</title>
        <meta name="description" content="Browse and manage your AI-generated videos." />
      </Helmet>

      {/* Header: title + search */}
      <div className="flex items-start justify-between gap-6 mb-10 max-w-[1100px] mx-auto w-full">
        <div>
          <h1 className="text-[22px] leading-tight font-medium tracking-tight text-foreground">Library</h1>
          <p className="mt-1 text-[14px] text-muted-foreground">Browse and manage your videos</p>
        </div>
        <div className="relative shrink-0 hidden md:block">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search library"
            className="h-10 w-[300px] rounded-full border border-border bg-white pl-10 pr-4 text-[14px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring"
          />
        </div>
      </div>

      {/* Body */}
      {isEmpty ? (
        <EmptyLibrary query={query} />
      ) : (
        <ProjectGrid
          workflows={visibleWorkflows}
          videos={visibleVideos}
          isLoading={isLoading}
          onLegacyVideoCleared={refetchLegacy}
        />
      )}
    </div>
  )
}

function EmptyLibrary({ query }: { query: string }) {
  return (
    <div className="max-w-[1100px] mx-auto w-full">
      {/* Decorative skeleton cards — give the page structure even when empty */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              // Fixed height (not aspect-ratio) so cards don't grow taller as
              // the viewport widens and push the empty state around.
              'h-[280px] rounded-[20px] bg-gradient-to-b from-zinc-100 to-background',
              // Mobile shows only the first cell; tablet 2, desktop 3.
              i === 1 && 'hidden sm:block',
              i === 2 && 'hidden lg:block',
            )}
            aria-hidden
          />
        ))}
      </div>

      {/* Empty-state CTA sits a fixed distance below the cards so its position
          stays stable as the viewport resizes. */}
      <div className="flex flex-col items-center text-center mt-8">
        <p className="text-[15px] text-muted-foreground">
          {query ? `No videos match "${query}".` : 'No videos yet. Start chatting to generate videos.'}
        </p>
        <Link
          to="/"
          className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-full border border-border text-[14px] font-medium text-foreground hover:bg-muted/60 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Video
        </Link>
      </div>
    </div>
  )
}
